import {
  DEFAULT_LOCALE,
  detectLocaleFromAcceptLanguage,
  isSupportedLocale,
  type Locale,
} from '@syncanix/shared';

/**
 * Initial-locale resolution + persistence for the dashboard (AF-10.M2).
 *
 * Resolution order: saved preference → browser `navigator.language` → EN.
 * Mirrors the `EnvironmentProvider` / `ThemeProvider` persistence convention
 * (`window.localStorage`, `syncanix:<thing>` key, SSR/test-safe `window`
 * guard). Pure of React — consumed by `Providers.tsx`, which passes the
 * resolved locale as `initialLocale` and `persistLocale` as `onLocaleChange`.
 */
export const LOCALE_STORAGE_KEY = 'syncanix:locale';

export function resolveInitialLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  // 1. An explicit saved preference always wins (the user chose it).
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored !== null && isSupportedLocale(stored)) return stored;

  // 2. Otherwise infer from the browser. `navigator.language` is a single
  //    value (e.g. "es-419"), which is a degenerate Accept-Language string —
  //    the shared matcher resolves it to a supported Locale or DEFAULT_LOCALE.
  return detectLocaleFromAcceptLanguage(window.navigator.language);
}

export function persistLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}
